// ── Certificate section (Wave 9 → FR-CERT wired; participant OWN-ONLY) ───────
// The certificate READ is NOW LIVE. This section reads the participant's OWN
// certificate(s) via GET /certificates — the server scope-isolates the read to
// the participant's OWN certificate(s). We render status + issued-at and an
// HONEST "document pending" when artifactRef is null (NO fabricated download
// link). The participant never sees a cross-participant list.
//
// ISOLATION (FR-PDASH-001): own data only — server-isolated. NO-FABRICATION: a
// null artifactRef shows "document pending", never a fake download URL.

import { CollectionPagination, CollectionToolbar, DataTableV2, IconDoc } from "@vjal/ui";

import { useHonestQuery } from "../../components/honest-state";
import type { CoachingCertificatesResponseDto } from "../../services/coaching-readiness-dtos";
import { ParticipantSectionShell } from "./participant-section-shell";
import { participantQueryKeys, readOwnCertificates } from "./participant-dashboard-helpers";
import {
  useParticipantCertificateCollection,
  type ParticipantCertificateRow,
} from "./participant-certificate-collection";


export function ParticipantCertificateSection() {
  const query = useHonestQuery<CoachingCertificatesResponseDto>(
    participantQueryKeys.certificates(),
    readOwnCertificates,
    { isEmpty: (data) => (data.certificates ?? []).length === 0 }
  );

  const rows = (query.data?.certificates ?? []) as ParticipantCertificateRow[];
  const collection = useParticipantCertificateCollection(rows);

  return (
    <ParticipantSectionShell
      title="Certificate"
      subtitle="Your certificate status (your data only)."
      posture={query.posture}
      gate={query.gate}
      reference={query.reference}
      onRetry={query.retry}
      emptyTitle="No certificate yet"
      emptyMessage="You don't have a certificate yet. Completing your program (including a passing capstone) issues one, and it appears here."
      icon={<IconDoc size={22} />}
      testId="participant-certificate-section"
    >
      <CollectionToolbar collection={collection} className="rounded-xl border border-line" stableChipNames />
      <div className="rounded-xl border border-line bg-white">
        <DataTableV2 collection={collection} caption="Your certificate" />
        {collection.uiState === "ready" && <CollectionPagination collection={collection} className="px-4" />}
      </div>
    </ParticipantSectionShell>
  );
}
